import { setUser } from '@sentry/react'
import { Action } from 'redux'
import { combineEpics, Epic, ofType, StateObservable } from 'redux-observable'
import { createStreamEpic } from '../../signalr/createStreamEpic'
import { fetchAuthTokenFailure } from '../auth/actions'
import { MarketClosedAction, marketCloseTimerTick } from './actions'
import { marketClosedDefinition } from './definition'
import {
  distinctUntilChanged,
  EMPTY,
  interval,
  map,
  of,
  switchMap,
  take
} from 'rxjs'
import { closeTimerCount, State } from './reducer'
import { getMarketClosed, getMarketClosedTimer } from './selectors'
import { State as WebSettingsState } from '../webSettings/reducer'
import { getIsAdmin } from '../webSettings/selectors'

type MarketEpicState = { market: State; webSettings: WebSettingsState }

const signOut = (state$: StateObservable<MarketEpicState>) => {
  const marketIsClosed = getMarketClosed(state$.value)
  if (marketIsClosed) {
    setUser(null)
    return of(fetchAuthTokenFailure('Markets closed'))
  }
  return EMPTY
}

const marketHasClosedEpic: Epic<Action, Action, MarketEpicState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<MarketClosedAction>('market.marketClosed'),
    distinctUntilChanged(),
    switchMap((action) => {
      if (!getIsAdmin(state$.value)) {
        if (document.visibilityState === 'hidden') {
          return signOut(state$)
        }
        if (action.payload.closed) {
          return interval(1000).pipe(
            map(marketCloseTimerTick),
            take(closeTimerCount)
          )
        }
      }
      return EMPTY
    })
  )

const signoutAfterMarketClosedEpic: Epic<Action, Action, MarketEpicState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('market.closeTimerTick'),
    switchMap(() => {
      const timeRemaining = getMarketClosedTimer(state$.value)
      if (timeRemaining) return EMPTY
      return signOut(state$)
    })
  )

export default combineEpics(
  createStreamEpic('settings.handleWindowSettings', marketClosedDefinition),
  marketHasClosedEpic,
  signoutAfterMarketClosedEpic
)
